import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { firebaseApp, firebaseConfig, isFirebaseConfigured, vapidKey } from "./firebase";
import { UserService } from "../services/UserService";
import { Settings, User } from "../storage/store";

// Push notifications through Firebase Cloud Messaging, as the app does in
// app/hooks/usePushNotification.js: after sign-in the dashboard asks for
// permission, gets this browser's FCM token and sends it to the server
// (POST user/fcmToken); signing out removes it again.

// public/firebase-messaging-sw.js shows notifications while the dashboard
// isn't in front. It gets the Firebase config in its URL, so the config only
// lives in firebase.js. Firebase's own worker scope keeps it off the app's pages.
const WORKER_URL = "/firebase-messaging-sw.js";
const WORKER_SCOPE = "/firebase-cloud-messaging-push-scope";

// Why push can't work in this browser, or null when it can.
export const pushUnavailableReason = async () => {
  if (!isFirebaseConfigured) return "not-configured";
  const supported = "Notification" in window && (await isSupported().catch(() => false));
  return supported ? null : "unsupported";
};

const messaging = () => getMessaging(firebaseApp());

// Resolves once the worker is active; Firebase can't subscribe before then.
// Gives up after 20 s, so the switch can't wait forever.
const whenActive = (registration) =>
  new Promise((resolve, reject) => {
    if (registration.active) {
      resolve(registration);
      return;
    }
    const worker = registration.installing || registration.waiting;
    const timer = setTimeout(() => reject(new Error("The notification service worker didn't start in time")), 20000);
    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") {
        clearTimeout(timer);
        resolve(registration);
      }
      if (worker.state === "redundant") {
        clearTimeout(timer);
        reject(new Error("The notification service worker didn't install"));
      }
    });
  });

const startWorker = async (url) => whenActive(await navigator.serviceWorker.register(url, { scope: WORKER_SCOPE }));

// Browsers sometimes fail to start a new worker the first time ("ServiceWorker
// cannot be started"); a second try a moment later usually works.
const registerWorker = async () => {
  const url = `${WORKER_URL}?config=${encodeURIComponent(JSON.stringify(firebaseConfig))}`;
  try {
    return await startWorker(url);
  } catch (error) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return startWorker(url);
  }
};

// Runs one step of turning push on and marks a failure with the step's name
// (error.pushStep), so the switch can say what went wrong.
const step = async (name, run) => {
  try {
    return await run();
  } catch (error) {
    const failure = error instanceof Error ? error : new Error(String(error?.message ?? error));
    failure.pushStep = name;
    throw failure;
  }
};

// Gets this browser's token and sends it to the server.
const registerToken = async () => {
  const registration = await step("worker", registerWorker);
  const token = await step("token", () => getToken(messaging(), { vapidKey, serviceWorkerRegistration: registration }));
  if (!token) throw Object.assign(new Error("Firebase returned no token"), { pushStep: "token" });
  await step("server", () => UserService.updateFcmToken({ fcmToken: token }));
  await User.setFcmToken(token);
};

// Stops pushes to this browser: the server forgets the token and the browser
// drops its push subscription. Call it while the user is still signed in.
export const forgetPushToken = async () => {
  const token = await User.getFcmToken();
  if (token) await UserService.deleteFcmToken({ fcmToken: token }).catch(() => {});
  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.getRegistration(WORKER_SCOPE);
    if (registration?.scope.endsWith(WORKER_SCOPE)) {
      const subscription = await registration.pushManager.getSubscription();
      await subscription?.unsubscribe();
      await registration.unregister();
    }
  }
  await User.clearFcmToken();
};

let synced = null;

// After sign-in: asks for permission once if this browser hasn't been asked,
// then keeps the token registered while notifications are allowed. Runs once
// per page load.
export const syncPushNotifications = () => {
  if (!synced) {
    synced = (async () => {
      if (await pushUnavailableReason()) return;
      const setting = Settings.getPNEnabled();
      if (setting === false) return;
      let permission = Notification.permission;
      if (permission === "default" && setting === null) {
        permission = await Notification.requestPermission().catch(() => "default");
      }
      if (permission === "granted") {
        await registerToken();
        Settings.setPNEnabled(true);
      } else if (permission === "denied") {
        Settings.setPNEnabled(false);
      }
    })().catch(() => {});
  }
  return synced;
};

// Whether the side menu switch shows push as on.
export const pushEnabled = () =>
  Settings.getPNEnabled() === true && "Notification" in window && Notification.permission === "granted";

// Turns push on from the Settings switch. Resolves to "enabled",
// "not-configured", "unsupported", "blocked" or "dismissed"; throws if the
// token couldn't be registered, with error.pushStep "worker", "token" or
// "server".
export const enablePushNotifications = async () => {
  if (!isFirebaseConfigured) return "not-configured";
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  // Ask before anything else is awaited: Safari and Firefox only show the
  // prompt while the click that turned the switch on still counts.
  const permission = await Notification.requestPermission().catch(() => "default");
  const reason = await pushUnavailableReason();
  if (reason) return reason;
  if (permission === "denied") {
    Settings.setPNEnabled(false);
    return "blocked";
  }
  if (permission !== "granted") return "dismissed";
  await registerToken();
  Settings.setPNEnabled(true);
  return "enabled";
};

export const disablePushNotifications = async () => {
  Settings.setPNEnabled(false);
  await forgetPushToken();
};

// Messages that arrive while the dashboard is in front (the app's
// messaging().onMessage). Resolves to a function that stops listening.
export const onForegroundMessage = async (callback) => {
  if (await pushUnavailableReason()) return () => {};
  return onMessage(messaging(), callback);
};
